import type { QueryClient } from "@tanstack/react-query"

import { limpiar as limpiarBitacora } from "./bitacora"
import { usarSesion } from "./sesion"

/**
 * Al cambiar de PERSONA —cerrar sesión, entrar con otra cuenta, sesión vencida— no
 * queda nada de la anterior en el navegador (05/11, pedido del usuario).
 *
 * ⚠ **El caso que lo pidió**: se cerró la sesión de administración, se entró con la de
 * una ejecutiva y `/admin/usuarios` enseñaba la lista de cuentas. El servidor le
 * respondía 403, pero la caché de TanStack Query seguía teniendo la respuesta que se le
 * dio al administrador, y la pantalla la pintaba. Cerrar sesión borraba la cookie y el
 * usuario, no lo que se había leído con ellos.
 *
 * ⚠ **Se vacía al CAMBIAR el usuario, no al salir**: así vale también para la sesión que
 * vence (un 401 la deja en `null`, ver `api/cliente.ts`) y para entrar con otra cuenta
 * sin haber pulsado «Salir». La suscripción corre ANTES de que React vuelva a pintar,
 * así que la pantalla nueva nunca llega a ver la caché vieja.
 *
 * Con la caché se va la bitácora de señales (`lib/bitacora.ts`): un reporte de falla de
 * la persona nueva no puede llevar las rutas y los errores de la anterior.
 */
export function vaciarAlCambiarDePersona(cliente: QueryClient): () => void {
  return usarSesion.subscribe((estado, previo) => {
    if (estado.usuario?.id === previo.usuario?.id) return
    cliente.clear()
    limpiarBitacora()
  })
}

/**
 * El servidor dijo que la sesión ya no vale (401): vencida, revocada o la contraseña
 * cambió. Se suelta aquí también, y `Protegida` manda al acceso.
 *
 * ⚠ **Sólo un 401 cierra la sesión**: el servidor lo reserva para eso. Una contraseña o
 * un PIN equivocados son 409 a propósito (`services/cofre.py`, `pin_cuentas.py`). Las
 * rutas de `/auth/` quedan fuera: el 401 del acceso es «credenciales incorrectas».
 */
export function sesionRechazada(ruta: string): void {
  if (ruta.startsWith("/api/v1/auth/")) return
  const { usuario, setUsuario } = usarSesion.getState()
  if (usuario !== null) setUsuario(null)
}
